// AI Generated Test
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { HotkeysService, Hotkey } from 'angular2-hotkeys';
import { ToastService } from '@monsido/angular-shared-components';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SpellingRepoService } from '@client/app/services/api/spelling-repo/spelling-repo.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';

@Component({
    selector: 'spelling-play',
    standalone: false,
    template: `
        <div class="col-48 inner-none spelling-play-container">
            <div class="col-6 text-center word-nav-btn vcenter" (click)="prevWord()">
                <span class="fas fa-angle-left fa-5x"></span>
            </div>
            <div class="col-35 inner-none vcenter text-center">
                <div class="outer-b-large outer-t-medium">
                    <span class="label outer-r-small inner-l-small inner-r-medium"
                          [class.label-info]="currentWord.classification === 'potential'"
                          [class.label-warning]="currentWord.classification !== 'potential'">&nbsp;</span>
                    <span>{{currentWord.classification === 'potential' ?
                          getPotentialMisspellingText() : getConfirmedMisspellingText()}}</span>
                </div>
                <div>
                    <div class="inner-t-small inner-b-small"
                         [class.label-info]="currentWord.classification === 'potential'"
                         [class.label-warning]="currentWord.classification !== 'potential'">
                        <h1 class="outer-t-small misspelling-word">
                            <span class="fas fa-spinner fa-spin fa-fw" aria-hidden="true" *ngIf="loading"></span>
                            {{currentWord.word}}
                        </h1>
                    </div>
                    <div class="word-meta inner-t-small inner-b-small">
                        Language: {{currentWord.language}} <br>
                        Appears on: {{currentWord.html_pages_count}} pages <br>
                        <a target="_blank" aria-describedby="linkOpensInNewTab"
                           [href]="'https://www.google.com/search?q=' + currentWord.word" class="btn">
                            <span class="fab fa-google"></span>&nbsp;Look up in Google
                        </a>
                    </div>
                </div>
            </div>
            <div class="col-6 text-center word-nav-btn vcenter" (click)="nextWord()">
                <span class="fas fa-angle-right fa-5x"></span>
            </div>
            <div class="outer-t-large text-center">
                <div class="btn btn-border-radius is-misspelling"
                     *ngIf="currentWord.classification === 'potential'"
                     [monModalConfirm]="'Are you sure you want to confirm the misspelling <strong>' + word + '</strong>?' | translate"
                     (monModalConfirmAction)="confirmMisspelling(currentWord)">
                    Is misspelling
                </div>
                <div class="btn btn-border-radius dictionary outer-l-small"
                     [monModalConfirm]="'Are you sure you want to add the word <strong>' + word + '</strong> to the dictionary?' | translate"
                     (monModalConfirmAction)="addToDictionary()">
                    <span class="fas fa-book"></span> Add to dictionary
                </div>
                <div class="btn btn-border-radius dictionary-all outer-l-small"
                     [monModalConfirm]="'Are you sure you want to add the word <strong>' + word + '</strong> to the dictionary for all languages?' | translate"
                     (monModalConfirmAction)="addToAllDictionaries()">
                    <span class="fas fa-book"></span> Add to dictionary for all languages
                </div>
            </div>
        </div>
    `,
})
export class SpellingPlayComponent implements OnInit, OnDestroy {
    @Input() words: any;
    @Input() language: any;
    @Input() search: any;
    @Input() type: any;

    currentWord: any = {};
    currentIndex: number = 0;
    totalPage: number = 0;
    domainId = '';
    loading: boolean = false;
    word = '';

    private hotkeys: Hotkey[] = [];

    constructor (
        private monDialog: MonDialog,
        private translateService: TranslateService,
        private toastService: ToastService,
        private spellingRepoService: SpellingRepoService,
        private hotkeysService: HotkeysService,
    ) {}

    ngOnInit () {
        this.currentWord = this.words[0] ? this.words[0] : {};
        this.currentIndex = 0;
        this.totalPage = Math.ceil(this.words.total / this.words.perPage);
        this.domainId = this.getCurrentDomainId();
        this.word = this.currentWord.word;
        this.hotkeysInit();
    }

    ngOnDestroy () {
        this.hotkeys.forEach(hotkey => this.hotkeysService.remove(hotkey));
    }

    private getCurrentDomainId (): string {
        const pathSegments = window.location.pathname.split('/');
        const domainIndex = pathSegments.indexOf('domain');
        return domainIndex >= 0 && pathSegments[domainIndex + 1] ? pathSegments[domainIndex + 1] : '';
    }

    hotkeysInit () {
        const rightHotkey = new Hotkey('right', (): boolean => {
            this.nextWord();
            return false;
        }, undefined, 'Show next word');

        const leftHotkey = new Hotkey('left', (): boolean => {
            this.prevWord();
            return false;
        }, undefined, 'Show previous word');

        this.hotkeys.push(rightHotkey, leftHotkey);
        this.hotkeysService.add(rightHotkey);
        this.hotkeysService.add(leftHotkey);
    }

    getPotentialMisspellingText (): string {
        return this.translateService.getString('Potential misspelling');
    }

    getConfirmedMisspellingText (): string {
        return this.translateService.getString('Confirmed misspelling');
    }

    nextWord (splice?: boolean) {
        if (this.loading) {
            return;
        }

        if (splice) {
            this.words.splice(this.currentIndex, 1);
        }

        if (this.currentIndex >= this.words.length - 1) {
            if (this.words.currentPage < this.totalPage) {
                this.getPages(parseInt(this.words.currentPage, 10) + 1, true);
            } else {
                this.getPages(1, true);
            }
        } else {
            this.currentIndex++;
            this.currentWord = this.words[this.currentIndex];
            this.word = this.currentWord.word;
        }
    }

    prevWord () {
        if (this.loading) {
            return;
        }
        if (this.currentIndex === 0) {
            if (this.words.currentPage > 1) {
                this.getPages(parseInt(this.words.currentPage, 10) - 1, false);
            } else {
                this.getPages(this.totalPage, false);
            }
        } else {
            this.currentIndex--;
            this.currentWord = this.words[this.currentIndex];
            this.word = this.currentWord.word;
        }
    }

    getPages (page: number, next: boolean) {
        const params: any = {
            domainId: parseInt(this.domainId, 10) || undefined,
            classification: this.type,
            page_fixed: false,
            language: this.language,
            page: page,
            page_size: this.words.perPage,
            search: this.search,
            sort_by: 'html_pages_count',
            sort_dir: 'desc',
        };

        this.loading = true;

        this.spellingRepoService.getAllErrors(params).then((data: any) => {
            this.loading = false;
            this.words = data;
            if (this.words.length === 0) {
                this.close();
            }
            if (next) {
                this.currentWord = this.words[0];
                this.currentIndex = 0;
            } else {
                this.currentWord = this.words[this.words.length - 1];
                this.currentIndex = this.words.length - 1;
            }
            this.word = this.currentWord.word;
        }).catch(() => {
            this.loading = false;
        });
    }

    close () {
        this.monDialog.closeAll('', '');
    }

    confirmMisspelling (word: any) {
        const params = {
            id: word.id,
            labels: [],
            classification: 'confirmed',
        };
        this.spellingRepoService.confirmWord(word.id, params).then(() => {
            this.toastService.success(this.translateService.getString('Word is confirmed as misspelling'));
            this.nextWord(true);
        }).catch(() => {});
    }

    addToDictionary () {
        const params = {
            word: this.currentWord.word,
            language: this.currentWord.language,
        };
        this.spellingRepoService.addToDictionary(params).then(() => {
            this.toastService.success(this.translateService.getString('Word is added to dictionary'));
            this.nextWord(true);
        }).catch(() => {});
    }

    addToAllDictionaries () {
        const params = {
            word: this.currentWord.word,
        };
        this.spellingRepoService.addToDictionary(params).then(() => {
            this.toastService.success(this.translateService.getString('Word is added to dictionary for all languages'));
            this.nextWord(true);
        }).catch(() => {});
    }
}
